import { useState } from 'react';
import { endOfDay } from 'date-fns';
import styled from 'styled-components';
import * as yup from 'yup';

import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input, { InputStyles } from 'components/shared/form/Input';
import StandardModal from 'components/shared/StandardModal';
import { FormProvider, useForm } from 'context/form';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { parseDate, simpleDashDate, simpleDate } from 'lib/date';
import { DenialListItem, Sex, SEX_DISPLAY } from 'models/DenialListItem';
import { createDenialListItem, DenialListItemMutationParams, updateDenialListItem } from 'services/api/denialListItems';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

const REASON_MAX_LENGTH = 5000;

type SexOption = { value: Sex; label: string };
const SEX_OPTIONS: SexOption[] = (Object.keys(SEX_DISPLAY) as Sex[]).map((value) => ({
  value,
  label: SEX_DISPLAY[value],
}));

type DenialListEntryFormValues = {
  patientName: string;
  dateOfBirth: Date | null;
  sex: SexOption | null;
  reason: string;
  dateAdded: Date | null;
};

type DenialListEntryFormMeta = {
  isSubmitting: boolean;
};

// "Not in the future" is checked against today evaluated at validation time (not module
// load) — the Date Added default is `new Date()` created when the modal opens, which would
// fail a max() pinned at import time and wedge the form as permanently invalid.
const notInFuture = (value?: Date | null) => !value || value <= endOfDay(new Date());

const denialListEntrySchema = yup.object({
  patientName: yup.string().trim().required(),
  dateOfBirth: yup
    .date()
    .nullable()
    .required()
    .typeError('Enter a valid date')
    .test('dob-not-future', 'Date of birth cannot be in the future', notInFuture),
  sex: yup.object({ value: yup.string(), label: yup.string() }).nullable().required(),
  reason: yup.string().nullable().max(REASON_MAX_LENGTH),
  dateAdded: yup
    .date()
    .nullable()
    .typeError('Enter a valid date')
    .test('date-added-not-future', 'Date added cannot be in the future', notInFuture),
});

export type DenialListEntryModalProps = {
  denialListItem?: DenialListItem;
  onClose: () => void;
};

export default function DenialListEntryModal({ denialListItem, onClose }: DenialListEntryModalProps) {
  const isEdit = !!denialListItem;

  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate } = useMutateWithToast({
    mutationFn: (params: DenialListItemMutationParams) =>
      isEdit ? updateDenialListItem({ id: denialListItem.id, ...params }) : createDenialListItem(params),
  });

  const handleSubmit = (
    values: FormValues<DenialListEntryFormValues>,
    _meta: FormMeta<DenialListEntryFormMeta>,
    helpers: FormHelpers<DenialListEntryFormValues, DenialListEntryFormMeta>
  ) => {
    helpers.setMeta('isSubmitting', true);

    const params: DenialListItemMutationParams = {
      patientName: (values.patientName ?? '').trim(),
      dateOfBirth: simpleDashDate(values.dateOfBirth) ?? '',
      sex: (values.sex?.value ?? 'unknown') as Sex,
      reason: values.reason?.trim() || null,
      dateAdded: simpleDashDate(values.dateAdded),
    };

    mutate(params, {
      onSuccess: () => {
        invalidateQueries({ denialListItems: true });
        addToast({
          text: isEdit
            ? `${params.patientName} successfully updated`
            : `${params.patientName} successfully added to denial list`,
        });
        onClose();
      },
      onSettled: () => {
        helpers.setMeta('isSubmitting', false);
      },
    });
  };

  const title = isEdit ? 'Edit Denial List Entry' : 'Add New Denial List Entry';

  return (
    <StandardModal title={title} onCancel={onClose}>
      <FormProvider<DenialListEntryFormValues, DenialListEntryFormMeta>
        onSubmit={handleSubmit}
        yupSchema={denialListEntrySchema}
        initialValues={{
          patientName: denialListItem?.patientName ?? '',
          dateOfBirth: parseDate(denialListItem?.dateOfBirth ?? null),
          sex: SEX_OPTIONS.find((option) => option.value === denialListItem?.sex) ?? null,
          reason: denialListItem?.reason ?? '',
          // Date Added defaults to today (the acting user's local/group day), editable on create + edit.
          dateAdded: isEdit ? parseDate(denialListItem?.dateAdded) : new Date(),
        }}
        initialMeta={{ isSubmitting: false }}>
        <DenialListEntryForm isEdit={isEdit} onClose={onClose} />
      </FormProvider>
    </StandardModal>
  );
}

function DenialListEntryForm({ isEdit, onClose }: { isEdit: boolean; onClose: () => void }) {
  const { values, errors, setValue, validateField, valid, dirty, meta, submitForm } = useForm<
    DenialListEntryFormValues,
    DenialListEntryFormMeta
  >();

  const [step, setStep] = useState<'form' | 'review'>('form');
  const isSubmitting = !!meta.isSubmitting;

  if (step === 'review') {
    return (
      <>
        <Description>
          {isEdit
            ? 'Review and confirm the details below before updating this patient on your global denial list.'
            : 'Review and confirm the details below before adding this patient to your global denial list.'}
        </Description>

        <ReviewList>
          <ReviewRow label='Patient Name' value={values.patientName?.trim()} />
          <ReviewRow label='Date of Birth' value={simpleDate(values.dateOfBirth)} />
          <ReviewRow label='Gender' value={values.sex?.label} />
          <ReviewRow label='Reason' value={values.reason?.trim() || null} />
          <ReviewRow label='Date Added' value={simpleDate(values.dateAdded)} />
        </ReviewList>

        <StandardModal.Actions>
          <Button variant='ghost' onClick={() => setStep('form')} disabled={isSubmitting}>
            Back
          </Button>
          <Button variant='primary' onClick={() => submitForm()} loading={isSubmitting} disabled={isSubmitting}>
            Submit
          </Button>
        </StandardModal.Actions>
      </>
    );
  }

  return (
    <>
      {!isEdit && (
        <Description>
          Fill out the fields below to add a new patient to your global denial list. Olio will automatically flag the
          patient on any new referrals that match.
        </Description>
      )}

      <Fields>
        <Field>
          <FieldLabel>Patient Name</FieldLabel>
          <Input
            value={values.patientName ?? ''}
            onChange={(e) => setValue('patientName', e.target.value)}
            placeholder='Patient Name'
            disabled={isSubmitting}
          />
        </Field>

        <HalfField>
          <FieldLabel>Date of Birth</FieldLabel>
          <DatePicker
            selected={values.dateOfBirth ?? null}
            onChange={(date) => setValue('dateOfBirth', date)}
            onBlur={() => validateField('dateOfBirth', values)}
            placeholder='MM/DD/YYYY'
            disabled={isSubmitting}
            maxDate={new Date()}
            peekNextMonth
            showMonthDropdown
            showYearDropdown
            dropdownMode='select'
            $hasError={!!errors.dateOfBirth}
            $fullWidth
          />
        </HalfField>

        <Field>
          <FieldLabel>Gender</FieldLabel>
          <Select<SexOption>
            options={SEX_OPTIONS}
            value={values.sex ?? null}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            onChange={(option) => setValue('sex', option)}
            placeholder='Gender'
            isSearchable={false}
            disabled={isSubmitting}
            dataTestId='gender-select'
          />
        </Field>

        <Field>
          <FieldLabel>Reason</FieldLabel>
          <TextArea
            value={values.reason ?? ''}
            onChange={(e) => setValue('reason', e.target.value)}
            placeholder='Add any additional context here'
            maxLength={REASON_MAX_LENGTH}
            rows={3}
            disabled={isSubmitting}
          />
        </Field>

        <HalfField>
          <FieldLabel>Date Added</FieldLabel>
          <DatePicker
            selected={values.dateAdded ?? null}
            onChange={(date) => setValue('dateAdded', date)}
            placeholder='MM/DD/YYYY'
            disabled={isSubmitting}
            maxDate={new Date()}
            $fullWidth
          />
        </HalfField>
      </Fields>

      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant='primary' onClick={() => setStep('review')} disabled={!valid || !dirty}>
          Next
        </Button>
      </StandardModal.Actions>
    </>
  );
}

function ReviewRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <Row>
      <RowLabel>{label}</RowLabel>
      <RowValue>{value || <>&mdash;</>}</RowValue>
    </Row>
  );
}

const Description = styled(BodySmall)`
  display: block;
  margin-bottom: 24px;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

// Date fields are half-width in the design (the date control occupies the left half of its
// row); text/select/textarea fields are full-width.
const HalfField = styled(Field)`
  width: 50%;
`;

const TextArea = styled.textarea`
  ${InputStyles}
  resize: vertical;
  font-family: inherit;
`;

const ReviewList = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
`;

const RowLabel = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black75};
`;

const RowValue = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black100};
`;
